import {
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { ReactElement, ReactNode } from 'react';

import type { AdminConsent, AdminDeletionRequest } from '@/admin/adminApi';
import { formatInstant } from '@/admin/users/accountPresentation';
import { Card } from '@/ui/kit';

import {
  consentLabel,
  consentStateLabel,
  consentUpdatedLabel,
  consentVersionLabel,
  deletionReasonLabel,
  deletionStatusLabel,
} from './privacyPresentation';

/**
 * Volet RGPD de la fiche de compte, en **lecture seule**.
 *
 * Ce que ces deux panneaux servent a faire : repondre a une personne qui
 * demande « qu'avez-vous de moi, et qu'ai-je accepte ? » sans ouvrir un
 * terminal. Ils n'offrent aucune modification, et c'est deliberé — un
 * consentement se donne et se retire par la personne elle-meme, depuis
 * l'application. Un back-office qui pourrait cocher un consentement a la place
 * de quelqu'un fabriquerait une preuve de consentement, ce qui est exactement
 * l'inverse de ce que le registre doit garantir.
 */

/** Section de la fiche : la carte du kit, dont le titre nomme la region. */
export const PrivacySection = ({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}): ReactElement => (
  <Card title={title} subtitle={description}>
    {children}
  </Card>
);

export const ConsentsPanel = ({ consents }: { consents: readonly AdminConsent[] }): ReactElement => (
  <PrivacySection
    title="Consentements"
    description="Ce que la personne a accepté ou refusé dans l’application, et depuis quand."
  >
    {consents.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        Aucun consentement n’est enregistré pour ce compte.
      </Typography>
    ) : (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Objet</TableCell>
              <TableCell>État</TableCell>
              <TableCell>Version du texte</TableCell>
              <TableCell>Mis à jour le</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {consents.map((consent) => (
              <TableRow key={consent.type}>
                <TableCell>{consentLabel(consent.type)}</TableCell>
                {/* Le mot, pas la couleur : « Accordé » / « Refusé » se lit en
                    noir et blanc comme au lecteur d'ecran. */}
                <TableCell>{consentStateLabel(consent)}</TableCell>
                <TableCell>{consentVersionLabel(consent)}</TableCell>
                <TableCell>{consentUpdatedLabel(consent)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </PrivacySection>
);

export const DeletionRequestPanel = ({
  request,
}: {
  request: AdminDeletionRequest | null;
}): ReactElement => (
  <PrivacySection
    title="Dernière demande de suppression de compte"
    description="Demande formulée par la personne depuis l’application, ou traitée pour elle depuis cet outil."
  >
    {request === null ? (
      <Typography variant="body2" color="text.secondary">
        Ce compte n’a jamais fait l’objet d’une demande.
      </Typography>
    ) : (
      <Stack spacing={0.75}>
        <Typography variant="body2">
          <strong>État :</strong> {deletionStatusLabel(request)}
        </Typography>
        <Typography variant="body2">
          <strong>Origine :</strong> {deletionReasonLabel(request)}
        </Typography>
        <Typography variant="body2">
          <strong>Demandée le :</strong> {formatInstant(request.requestedAt)}
        </Typography>
        <Typography variant="body2">
          <strong>Traitée le :</strong> {formatInstant(request.completedAt, 'Pas encore traitée')}
        </Typography>
        <Alert severity="info" variant="outlined">
          Une demande traitée rend le compte anonyme : les données de suivi de la famille, elles,
          restent en place pour la traçabilité des lots de lait.
        </Alert>
      </Stack>
    )}
  </PrivacySection>
);
